import { Injectable } from '@angular/core';
import {
  CreateBookmarkService,
  BookmarkService as QPBookmarkService,
} from 'qp-storefront';
import { ConfigService } from './config.service';
import { LocalStorageService } from './storage/local-storage.service';
import { KEYS } from '../utils/constants/storage.constants';
import { StorefrontPolicyService } from './firstlight/storefront-policy/storefront-policy.service';
import {
  BookmarkConfiguration,
  BookmarkService as FLBookmarkService,
  createBookmarkService,
  createPlaybackSessionBookmarker,
} from 'fl-bookmarks';
import { AuthService } from './auth/auth.service';
import { BookmarkPayload } from '../interfaces/bookmark';
import {
  AspectRatio,
  BookmarkData,
  CardLayout,
  ContainerVm,
  ResourceVm,
} from 'qp-ui-interface';
import { resourceAdaptor } from 'qp-adaptor';
import { getDeviceType } from '../utils/deviceUtils';
import { BehaviorSubject } from 'rxjs';
import { BookmarkMarker } from '../common/interfaces/common.interface';
import { NewrelicService } from 'src/app/analytics/newrelic/services/newrelic.service';

@Injectable({
  providedIn: 'root',
})
export class BookmarkService {
  private _qpInstance: QPBookmarkService;
  private _bookmarks = new BehaviorSubject<ContainerVm>(null);

  get bookmarks$() {
    return this._bookmarks.asObservable();
  }

  constructor(
    private readonly configService: ConfigService,
    private readonly localStorageService: LocalStorageService,
    private readonly stronefrontPolicyService: StorefrontPolicyService,
    private readonly authService: AuthService,
    private readonly newrelicService: NewrelicService
  ) {}

  private qpInstance() {
    if (!this._qpInstance) {
      this._qpInstance = CreateBookmarkService(
        this.configService.applicationConfig.bookmarkURL
      );
    }
    return this._qpInstance;
  }

  async getQpBookmarks() {
    try {
      const xauthToken = this.localStorageService.getItem(
        KEYS.EVERGENT_OVAT_TOKEN
      );
      const authToken = this.localStorageService.getItem(KEYS.PLATFORM_TOKEN);
      const pageSize = this.configService.continueWatchingPageSize();
      const result = await this.qpInstance().getBookmarkItems(
        xauthToken,
        authToken,
        true,
        this.stronefrontPolicyService.getPolicy(),
        pageSize
      );
      if (result?.length) {
        const data = this.bookmarkAdapter(result);
        this._bookmarks.next(data);
        return data;
      }
      return null;
    } catch (exception) {
      console.warn('bookmark error', exception);
      this.newrelicService.sendErrorEvent(
        {
          error: exception,
        },
        null,
        true
      );
      return null;
    }
  }

  async getQpBookmarksContainer(delay: number = 0) {
    if (!this.authService.isLoggedIn()) {
      return null;
    }
    if (delay) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    const data = await this.getQpBookmarks();
    if (data?.resources) {
      data.resources.forEach((resource) => {
        resource.apUrl = null;
        resource.ape = false;
        resource.shortDescription = null;
        resource.containerId = 'continueWatching';
        resource.containerName = { en: 'continueWatching' };
      });
      return data;
    }
    return null;
  }

  private formatBookmarkInfo(
    resources: ResourceVm[],
    bookmarkData: BookmarkData[]
  ): ResourceVm[] {
    return resources
      .map((resource) => {
        resource.containerId = 'continueWatching';
        resource.containerName = { en: 'continueWatching' };
        const bkmark = bookmarkData?.find((bm) => {
          if (
            (bm?.episodeId !== undefined &&
              bm?.episodeId.length > 5 &&
              bm?.episodeId === resource.id) ||
            bm.itemId === resource.id
          ) {
            return bm;
          }
          return null;
        });
        if (bkmark) {
          const percg = Math.ceil(
            bkmark ? Math.ceil(bkmark.offset / (10 * resource.offset)) : 0
          );
          return {
            ...resource,
            offset: percg,
          };
        }
        return null;
      })
      .filter((v) => v);
  }

  private bookmarkAdapter(_resources: BookmarkData[]): ContainerVm {
    let cwContainer = {
      aspectRatio: '16x9' as AspectRatio,
      id: 'cvm-ContinueWatching',
      layout: 'carousel' as CardLayout,
      name: 'ContinueWatching',
      sourceType: 'continue_watching',
      resources: [],
      localizedName: {
        en: { n: 'Continue Watching' },
      },
    };
    const continueWatchingResource = _resources.filter(
      (status) => status?.content_status === 'found'
    );
    let resources = continueWatchingResource.map((item) => {
      return resourceAdaptor(
        this.configService.applicationConfig.imageResizeURL,
        item.info,
        getDeviceType(),
        '0-16x9'
      );
    });
    resources = this.formatBookmarkInfo(resources, _resources);
    return {
      ...cwContainer,
      resources,
    };
  }

  async getBookmark(contentId: string) {
    try {
      return await this.authService.bookmarkService.getBookmark(contentId);
    } catch (exception) {
      console.warn('Detail bookmark error', exception);
      this.newrelicService.sendErrorEvent(
        {
          error: exception,
        },
        null,
        true
      );
      return null;
    }
  }

  async deleteBookmark(contentId: string) {
    try {
      await this.authService.bookmarkService.deleteBookmark(contentId);
      return true;
    } catch (exception) {
      this.newrelicService.sendErrorEvent({
        error: exception,
      });
      return false;
    }
  }

  async putBookmark(payload: BookmarkPayload) {
    try {
      await this.authService.bookmarkService.putBookmark(
        payload.contentId,
        payload.offset,
        payload.seasonId,
        payload.episodeId,
        payload.mode,
        payload.musicId,
        payload.seasonNumber,
        payload.episodeNumber
      );
      return true;
    } catch (exception) {
      this.newrelicService.sendErrorEvent({
        error: exception,
      });
      return false;
    }
  }

  watchPercent(bkmarkOffset: number, resourceOffset: number) {
    return Math.ceil(
      bkmarkOffset ? Math.ceil(bkmarkOffset / (10 * resourceOffset)) : 0
    );
  }

  bookmarkMarker(marker: BookmarkMarker) {
    const bookmarkConfiguration: BookmarkConfiguration = {
      endPointUrl: this.configService.applicationConfig.bookmarkURL,
      syncIntervalMS: parseInt(
        this.configService.applicationConfig.bookmarkSyncIntervalMs,
        10
      ),
      thresholdPercentage: parseInt(
        this.configService.applicationConfig.bookmarkDeleteThreshold,
        10
      ),
    };
    return createPlaybackSessionBookmarker(
      marker.contentId,
      bookmarkConfiguration,
      marker.platformAuthorizer,
      marker.seasonId,
      marker.episodeId,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      this.authService.bookmarkService
    );
  }
}
